import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const CustomerToolbar = ({
  selectedCustomers,
  onAddCustomer,
  onBulkAction,
  onExport,
  totalCustomers,
}) => {
  const { t } = useTranslation();
  const [bulkActionOpen, setBulkActionOpen] = useState(false);

  const bulkActions = [
    { value: "export", label: t("exportSelected"), icon: "Download" },
    { value: "activate", label: t("activateCustomers"), icon: "User Check" },
    { value: "deactivate", label: t("deactivateCustomers"), icon: "User X" },
    {
      value: "delete",
      label: t("deleteCustomers"),
      icon: "Trash2",
      destructive: true,
    },
  ];

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedCustomers);
    setBulkActionOpen(false);
  };

  return (
    <div className="bg-white border-b border-border p-4">
      <div className="flex items-center justify-between">
        {/* Left Section - Title and Selection Info */}
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {t("customerManagement")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {selectedCustomers.length > 0
                ? `${selectedCustomers.length} ${t("customersSelected", {
                    count: selectedCustomers.length,
                  })} من ${totalCustomers} العملاء المحددين`
                : t("", { total: totalCustomers })}
            </p>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center space-x-3 gap-3">
          {/* Bulk Actions Dropdown */}
          {selectedCustomers.length > 0 && (
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setBulkActionOpen(!bulkActionOpen)}
                className="flex items-center space-x-2"
              >
                <Icon name="MoreHorizontal" size={16} />
                <span>
                  {t("bulkActions")} ({selectedCustomers.length})
                </span>
                <Icon name="ChevronDown" size={16} />
              </Button>

              {bulkActionOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-md shadow-elevation-3 border border-border z-50">
                  <div className="py-2">
                    {bulkActions.map((action) => (
                      <button
                        key={action.value}
                        onClick={() => handleBulkAction(action.value)}
                        className={`w-full px-4 py-2 text-left text-sm transition-micro flex items-center space-x-2 ${
                          action.destructive
                            ? "text-error hover:bg-error hover:bg-opacity-10"
                            : "text-foreground hover:bg-muted"
                        }`}
                      >
                        <Icon name={action.icon} size={16} />
                        <span>{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export Button */}
          <Button
            variant="outline"
            onClick={onExport}
            className="flex items-center"
          >
            <Icon name="Download" size={16} />
            <span className="hidden sm:inline pr-2 pl-2">{t("export")}</span>
          </Button>

          {/* Add Customer Button */}
          <Button
            variant="default"
            onClick={onAddCustomer}
            className="flex items-center"
          >
            <Icon name="Users" size={18} />
            <span className="pr-2 pl-2">{t("addCustomer")}</span>
          </Button>
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectedCustomers.length > 0 && (
        <div className="mt-4 p-3 bg-primary bg-opacity-10 rounded-md border border-primary border-opacity-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-primary" />
              <span className="text-sm font-medium text-primary">
                {selectedCustomers.length}{" "}
                {t("customersSelected", { count: selectedCustomers.length })}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBulkAction("export")}
                className="text-primary hover:bg-primary hover:bg-opacity-10"
              >
                <Icon name="Download" size={14} className="mr-1" />
                {t("export")}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBulkAction("activate")}
                className="text-success hover:bg-success hover:bg-opacity-10"
              >
                <Icon name="User Check" size={14} className="mr-1" />
                {t("activateCustomers")}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBulkAction("deactivate")}
                className="text-secondary hover:bg-secondary hover:bg-opacity-10"
              >
                <Icon name="User X" size={14} className="mr-1" />
                {t("deactivateCustomers")}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBulkAction("delete")}
                className="text-error hover:bg-error hover:bg-opacity-10"
              >
                <Icon name="Trash2" size={14} className="mr-1" />
                {t("deleteCustomers")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {bulkActionOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setBulkActionOpen(false)}
        />
      )}
    </div>
  );
};

export default CustomerToolbar;
